import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';

// Import side-effect: registers the contracts with the runtime registry.
// Modeled on useClienteAcessoPortal.ts:241-346 — every contract this
// composable's calls are annotated with must be imported for its side
// effect before the first request fires, or `validate()` throws
// "unknown contract" at runtime.
import '../contracts/So_ServiceFiscalMissingResponse';
import '../contracts/So_ServiceFiscalResponse';
import '../contracts/So_ServiceFiscalSalvarResponse';
import '../contracts/So_ServiceFiscalInput';
import '../contracts/So_ProductFiscalMissingResponse';
import '../contracts/So_ProductFiscalResponse';
import '../contracts/So_ProductFiscalSalvarResponse';
import '../contracts/So_ProductFiscalInput';
import '../contracts/So_AdditionalServiceFiscalMissingResponse';
import '../contracts/So_AdditionalServiceFiscalResponse';
import '../contracts/So_AdditionalServiceFiscalSalvarResponse';
import '../contracts/So_AdditionalServiceFiscalInput';

import type { So_ServiceFiscalMissingRow } from '../contracts/So_ServiceFiscalMissingRow';
import type { So_ServiceFiscalEffectiveRow } from '../contracts/So_ServiceFiscalEffectiveRow';
import type { So_ServiceFiscalRow } from '../contracts/So_ServiceFiscalRow';
import type { So_ServiceFiscalInput } from '../contracts/So_ServiceFiscalInput';
import type { So_ProductFiscalMissingRow } from '../contracts/So_ProductFiscalMissingRow';
import type { So_ProductFiscalEffectiveRow } from '../contracts/So_ProductFiscalEffectiveRow';
import type { So_ProductFiscalRow } from '../contracts/So_ProductFiscalRow';
import type { So_ProductFiscalInput } from '../contracts/So_ProductFiscalInput';
import type { So_AdditionalServiceFiscalMissingRow } from '../contracts/So_AdditionalServiceFiscalMissingRow';
import type { So_AdditionalServiceFiscalEffectiveRow } from '../contracts/So_AdditionalServiceFiscalEffectiveRow';
import type { So_AdditionalServiceFiscalRow } from '../contracts/So_AdditionalServiceFiscalRow';
import type { So_AdditionalServiceFiscalInput } from '../contracts/So_AdditionalServiceFiscalInput';

/**
 * useSOCatalogFiscal — the catalog-fiscal sidecar composable (LC116/NCM and
 * friends) for services and products.
 *
 * NOT built on the `useSOCatalog(controllerName)` factory: that factory
 * assumes `lista`/`kv` endpoints and never threads `inputContract` /
 * `responseContract` through its calls. This sidecar has `missing` (not
 * `lista`), `get/{id}?franchise_id=`, and every one of its three endpoints
 * per domain (missing/get/salvar) is contract-registered — so this
 * composable is written by hand, following the shape of
 * useClienteAcessoPortal.ts.
 *
 * OVERRIDE SEMANTICS (read before touching `salvar*`): each row's
 * `franchise_id` is either null (GLOBAL — applies to every franchise) or one
 * franchise's id (an OVERRIDE for that franchise only). The effective read is
 * COALESCE(franchise override, global), field by field. `is_override` on the
 * Effective contract means only "a franchise-specific row exists" — NOT "this
 * particular field's value came from the franchise". Callers must not claim
 * field-level provenance the backend does not expose (So_service_fiscal_model
 * ::getEffective(), ~L130-160).
 *
 * PAYLOAD OMISSION RULE (the one that protects data): `salvarServiceFiscal` /
 * `salvarProductFiscal` take a `fields` bag where an untouched field is
 * `undefined`. Untouched keys are pruned before the request — so saving in
 * the `franquia` scope without touching an inherited field never freezes
 * that global value into the franchise's row (which would silently stop
 * future edits to the global from propagating). The backend supports this:
 * `So_service_fiscal_model::save()` only writes a column that is
 * `array_key_exists($col, $data)` — a JS key that was never set behaves the
 * same as one explicitly omitted.
 */

export type FiscalScope = 'global' | 'franquia';

export interface ServiceFiscalFields {
  lc116_code?: string;
  municipal_service_code?: string;
  iss_aliquota?: number;
  /** UI-facing boolean. Converted to the DB tinyint at the composable boundary. */
  iss_retido_default?: boolean;
  municipio_incidencia_ibge?: string;
  cnae?: string;
  /** NFS-e padrao NACIONAL only (E16) — 6-digit codigo_tributacao_nacional_iss. */
  codigo_tributacao_nacional?: string;
}

export interface ProductFiscalFields {
  ncm?: string;
  cest?: string;
  cfop_dentro_uf?: string;
  cfop_fora_uf?: string;
  cst?: string;
  csosn?: string;
  origem?: number;
  unidade_tributavel?: string;
}

/** Same shape as ServiceFiscalFields — additional services are a service for
 *  fiscal purposes (E13). Kept as its own named type rather than reusing
 *  ServiceFiscalFields so the two domains can diverge independently later. */
export interface AdditionalServiceFiscalFields {
  lc116_code?: string;
  municipal_service_code?: string;
  iss_aliquota?: number;
  iss_retido_default?: boolean;
  municipio_incidencia_ibge?: string;
  cnae?: string;
  /** NFS-e padrao NACIONAL only (E16) — 6-digit codigo_tributacao_nacional_iss. */
  codigo_tributacao_nacional?: string;
}

/**
 * Typed Data Layer boundary for `iss_retido_default`: DB tinyint (0/1/undefined)
 * <-> UI boolean. Kept as two named one-directional functions — collapsing
 * read+write into a single round-trip helper would let an identity bug
 * (e.g. always returning the input unchanged) pass unnoticed.
 */
export function tinyintToBoolean(v?: number): boolean {
  return v === 1;
}

export function booleanToTinyint(v: boolean): number {
  return v ? 1 : 0;
}

/**
 * Drop every key whose value is `undefined`. Used to build salvar() payloads
 * so an untouched field never appears in the request body at all — not even
 * as `key: undefined` — which is what the omission rule (see file docblock)
 * depends on: `Object.hasOwn` / `Object.keys` must not see the key.
 */
function pruneUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const key of Object.keys(obj) as (keyof T)[]) {
    if (obj[key] !== undefined) {
      out[key] = obj[key];
    }
  }
  return out;
}

export function useSOCatalogFiscal() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // ── Services ──────────────────────────────────────────────────────────

  /**
   * GET /so/service_fiscal/missing?franchise_id=N — readiness list. Requires
   * franchise_id (the backend rejects the call without it), so a caller with
   * no franchise selected gets [] without a wasted request.
   */
  async function carregarPendenciasServicos(
    franchiseId?: number | null
  ): Promise<So_ServiceFiscalMissingRow[]> {
    if (franchiseId == null) {
      log('useSOCatalogFiscal', 'carregarPendenciasServicos', 'Sem franquia selecionada, não chamando');
      return [];
    }
    isLoading.value = true;
    error.value = null;
    try {
      const resp = await httpService.get(
        `/index.php/so/service_fiscal/missing?franchise_id=${franchiseId}`,
        { responseContract: 'So_ServiceFiscalMissingResponse' }
      );
      if (!resp.success) {
        throw new Error(resp.error || 'Erro ao buscar pendências fiscais de serviços');
      }
      return resp.dados || [];
    } catch (err) {
      logError('useSOCatalogFiscal', 'carregarPendenciasServicos', 'Error', err);
      error.value = (err as Error).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /** GET /so/service_fiscal/get/{id}?franchise_id=N — effective (merged) read. */
  async function getServiceFiscal(
    serviceId: number,
    franchiseId?: number | null
  ): Promise<So_ServiceFiscalEffectiveRow | null> {
    const qs = franchiseId != null ? `?franchise_id=${franchiseId}` : '';
    isLoading.value = true;
    error.value = null;
    try {
      const resp = await httpService.get(
        `/index.php/so/service_fiscal/get/${serviceId}${qs}`,
        { responseContract: 'So_ServiceFiscalResponse' }
      );
      if (!resp.success) {
        throw new Error(resp.error || 'Erro ao buscar dados fiscais do serviço');
      }
      return resp.dados ?? null;
    } catch (err) {
      logError('useSOCatalogFiscal', 'getServiceFiscal', 'Error', err);
      error.value = (err as Error).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * POST /so/service_fiscal/salvar — writes the global row when scope is
   * 'global' (so_service_fiscal_franchise_id absent from the body) or the
   * franchise's override when scope is 'franquia' (franchise_id included).
   * Only `fields` the caller actually set cross the wire (see file docblock).
   */
  async function salvarServiceFiscal(params: {
    serviceId: number;
    scope: FiscalScope;
    franchiseId?: number | null;
    fields: ServiceFiscalFields;
  }): Promise<So_ServiceFiscalRow> {
    const { serviceId, scope, franchiseId, fields } = params;

    const rawBody: Record<string, unknown> = {
      so_service_fiscal_service_id: serviceId,
      so_service_fiscal_lc116_code: fields.lc116_code,
      so_service_fiscal_municipal_service_code: fields.municipal_service_code,
      so_service_fiscal_iss_aliquota: fields.iss_aliquota,
      so_service_fiscal_iss_retido_default:
        fields.iss_retido_default !== undefined ? booleanToTinyint(fields.iss_retido_default) : undefined,
      so_service_fiscal_municipio_incidencia_ibge: fields.municipio_incidencia_ibge,
      so_service_fiscal_cnae: fields.cnae,
      so_service_fiscal_codigo_tributacao_nacional: fields.codigo_tributacao_nacional,
    };
    // franchise_id membership is controlled by scope, never by "was it set" —
    // a global save must never carry the key even as undefined.
    if (scope === 'franquia') {
      rawBody.so_service_fiscal_franchise_id = franchiseId ?? undefined;
    }
    const body = pruneUndefined(rawBody) as unknown as So_ServiceFiscalInput;

    isLoading.value = true;
    error.value = null;
    try {
      const resp = await httpService.post('/index.php/so/service_fiscal/salvar', body, {
        inputContract: 'So_ServiceFiscalInput',
        responseContract: 'So_ServiceFiscalSalvarResponse',
      });
      if (!resp.success || !resp.dados) {
        throw new Error(resp.error || 'Erro ao salvar dados fiscais do serviço');
      }
      return resp.dados;
    } catch (err) {
      logError('useSOCatalogFiscal', 'salvarServiceFiscal', 'Error', err);
      error.value = (err as Error).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // ── Products (mirrors the service trio above) ────────────────────────────
  //
  // No removerServiceFiscal/removerProductFiscal here: this composable was
  // written with one (POST /*/remover — takes the fiscal ROW id, not the
  // service/product id). No component in this domain calls it, and none
  // COULD: the Effective contract (what every reader in this domain gets
  // back) never carries so_service_fiscal_id / so_product_fiscal_id, only
  // the merged field values — there is no id in scope to pass. The pair was
  // removed rather than left as an unreachable affordance; if a delete
  // surface is added later, it needs its own read path that actually
  // exposes the fiscal row id first.

  async function carregarPendenciasProdutos(
    franchiseId?: number | null
  ): Promise<So_ProductFiscalMissingRow[]> {
    if (franchiseId == null) {
      log('useSOCatalogFiscal', 'carregarPendenciasProdutos', 'Sem franquia selecionada, não chamando');
      return [];
    }
    isLoading.value = true;
    error.value = null;
    try {
      const resp = await httpService.get(
        `/index.php/so/product_fiscal/missing?franchise_id=${franchiseId}`,
        { responseContract: 'So_ProductFiscalMissingResponse' }
      );
      if (!resp.success) {
        throw new Error(resp.error || 'Erro ao buscar pendências fiscais de produtos');
      }
      return resp.dados || [];
    } catch (err) {
      logError('useSOCatalogFiscal', 'carregarPendenciasProdutos', 'Error', err);
      error.value = (err as Error).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function getProductFiscal(
    productId: number,
    franchiseId?: number | null
  ): Promise<So_ProductFiscalEffectiveRow | null> {
    const qs = franchiseId != null ? `?franchise_id=${franchiseId}` : '';
    isLoading.value = true;
    error.value = null;
    try {
      const resp = await httpService.get(
        `/index.php/so/product_fiscal/get/${productId}${qs}`,
        { responseContract: 'So_ProductFiscalResponse' }
      );
      if (!resp.success) {
        throw new Error(resp.error || 'Erro ao buscar dados fiscais do produto');
      }
      return resp.dados ?? null;
    } catch (err) {
      logError('useSOCatalogFiscal', 'getProductFiscal', 'Error', err);
      error.value = (err as Error).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function salvarProductFiscal(params: {
    productId: number;
    scope: FiscalScope;
    franchiseId?: number | null;
    fields: ProductFiscalFields;
  }): Promise<So_ProductFiscalRow> {
    const { productId, scope, franchiseId, fields } = params;

    const rawBody: Record<string, unknown> = {
      so_product_fiscal_product_id: productId,
      so_product_fiscal_ncm: fields.ncm,
      so_product_fiscal_cest: fields.cest,
      so_product_fiscal_cfop_dentro_uf: fields.cfop_dentro_uf,
      so_product_fiscal_cfop_fora_uf: fields.cfop_fora_uf,
      so_product_fiscal_cst: fields.cst,
      so_product_fiscal_csosn: fields.csosn,
      so_product_fiscal_origem: fields.origem,
      so_product_fiscal_unidade_tributavel: fields.unidade_tributavel,
    };
    if (scope === 'franquia') {
      rawBody.so_product_fiscal_franchise_id = franchiseId ?? undefined;
    }
    const body = pruneUndefined(rawBody) as unknown as So_ProductFiscalInput;

    isLoading.value = true;
    error.value = null;
    try {
      const resp = await httpService.post('/index.php/so/product_fiscal/salvar', body, {
        inputContract: 'So_ProductFiscalInput',
        responseContract: 'So_ProductFiscalSalvarResponse',
      });
      if (!resp.success || !resp.dados) {
        throw new Error(resp.error || 'Erro ao salvar dados fiscais do produto');
      }
      return resp.dados;
    } catch (err) {
      logError('useSOCatalogFiscal', 'salvarProductFiscal', 'Error', err);
      error.value = (err as Error).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // ── Additional services (mirrors the service trio — E13) ─────────────────

  async function carregarPendenciasServicosAdicionais(
    franchiseId?: number | null
  ): Promise<So_AdditionalServiceFiscalMissingRow[]> {
    if (franchiseId == null) {
      log('useSOCatalogFiscal', 'carregarPendenciasServicosAdicionais', 'Sem franquia selecionada, não chamando');
      return [];
    }
    isLoading.value = true;
    error.value = null;
    try {
      const resp = await httpService.get(
        `/index.php/so/additional_service_fiscal/missing?franchise_id=${franchiseId}`,
        { responseContract: 'So_AdditionalServiceFiscalMissingResponse' }
      );
      if (!resp.success) {
        throw new Error(resp.error || 'Erro ao buscar pendências fiscais de serviços adicionais');
      }
      return resp.dados || [];
    } catch (err) {
      logError('useSOCatalogFiscal', 'carregarPendenciasServicosAdicionais', 'Error', err);
      error.value = (err as Error).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function getAdditionalServiceFiscal(
    additionalServiceId: number,
    franchiseId?: number | null
  ): Promise<So_AdditionalServiceFiscalEffectiveRow | null> {
    const qs = franchiseId != null ? `?franchise_id=${franchiseId}` : '';
    isLoading.value = true;
    error.value = null;
    try {
      const resp = await httpService.get(
        `/index.php/so/additional_service_fiscal/get/${additionalServiceId}${qs}`,
        { responseContract: 'So_AdditionalServiceFiscalResponse' }
      );
      if (!resp.success) {
        throw new Error(resp.error || 'Erro ao buscar dados fiscais do serviço adicional');
      }
      return resp.dados ?? null;
    } catch (err) {
      logError('useSOCatalogFiscal', 'getAdditionalServiceFiscal', 'Error', err);
      error.value = (err as Error).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function salvarAdditionalServiceFiscal(params: {
    additionalServiceId: number;
    scope: FiscalScope;
    franchiseId?: number | null;
    fields: AdditionalServiceFiscalFields;
  }): Promise<So_AdditionalServiceFiscalRow> {
    const { additionalServiceId, scope, franchiseId, fields } = params;

    const rawBody: Record<string, unknown> = {
      so_additional_service_fiscal_additional_service_id: additionalServiceId,
      so_additional_service_fiscal_lc116_code: fields.lc116_code,
      so_additional_service_fiscal_municipal_service_code: fields.municipal_service_code,
      so_additional_service_fiscal_iss_aliquota: fields.iss_aliquota,
      so_additional_service_fiscal_iss_retido_default:
        fields.iss_retido_default !== undefined ? booleanToTinyint(fields.iss_retido_default) : undefined,
      so_additional_service_fiscal_municipio_incidencia_ibge: fields.municipio_incidencia_ibge,
      so_additional_service_fiscal_cnae: fields.cnae,
      so_additional_service_fiscal_codigo_tributacao_nacional: fields.codigo_tributacao_nacional,
    };
    // franchise_id membership is controlled by scope, never by "was it set" —
    // a global save must never carry the key even as undefined.
    if (scope === 'franquia') {
      rawBody.so_additional_service_fiscal_franchise_id = franchiseId ?? undefined;
    }
    const body = pruneUndefined(rawBody) as unknown as So_AdditionalServiceFiscalInput;

    isLoading.value = true;
    error.value = null;
    try {
      const resp = await httpService.post('/index.php/so/additional_service_fiscal/salvar', body, {
        inputContract: 'So_AdditionalServiceFiscalInput',
        responseContract: 'So_AdditionalServiceFiscalSalvarResponse',
      });
      if (!resp.success || !resp.dados) {
        throw new Error(resp.error || 'Erro ao salvar dados fiscais do serviço adicional');
      }
      return resp.dados;
    } catch (err) {
      logError('useSOCatalogFiscal', 'salvarAdditionalServiceFiscal', 'Error', err);
      error.value = (err as Error).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  return {
    isLoading,
    error,
    carregarPendenciasServicos,
    getServiceFiscal,
    salvarServiceFiscal,
    carregarPendenciasProdutos,
    getProductFiscal,
    salvarProductFiscal,
    carregarPendenciasServicosAdicionais,
    getAdditionalServiceFiscal,
    salvarAdditionalServiceFiscal,
  };
}
